<template>
  <div class="ws-details">
    <dl class="ws-details__list">
      <template v-for="row in rows">
        <dt :key="`${row.label}-label`" class="ws-details__label">{{ row.label }}</dt>
        <dd :key="`${row.label}-value`" class="ws-details__value" :title="row.title || null">
          <template v-if="row.chip">
            <v-chip x-small :color="row.chipColor" :outlined="row.outlined">{{ row.value }}</v-chip>
            <v-icon v-if="row.pinned" x-small color="warning" class="ml-1" title="Pinned as current">mdi-pin</v-icon>
          </template>
          <template v-else>{{ row.value }}</template>
        </dd>
      </template>
    </dl>
    <div v-if="description" class="ws-details__description">
      <div class="ws-details__label">Description</div>
      <p>{{ description }}</p>
    </div>
  </div>
</template>

<script>
  import { $formatDate } from '@/utils';

  /**
   * Metadata of the document and of the version on screen. Empty fields are left out.
   * `shown` is what the viewer shows (an older version carries its own file, size and labels).
   */
  export default {
    name: 'WorkspaceDetailsTab',
    props: {
      document: { type: Object, required: true },
      shown: { type: Object, default: null },
      documentTypeName: { type: String, default: '' },
    },
    computed: {
      file() {
        return this.shown || this.document;
      },
      isOldVersion() {
        return !!this.shown?.viewRevisionId;
      },
      description() {
        return this.file.revisionDescription || this.document.description || '';
      },
      rows() {
        const doc = this.document;
        const file = this.file;
        const versionNumber = file.versionNumber ?? doc.currentVersionNumber;
        const scope = doc.facilityGroupId
          ? { value: `Group · ${doc.facilityGroupName || 'Facility group'}`, title: 'Shared across all facilities in the group' }
          : { value: doc.assetName ? `Facility · ${doc.assetName}` : 'Facility', title: 'Available only in this facility' };
        return [
          { label: 'Document no.', value: doc.documentNumber },
          { label: 'Title', value: doc.documentTitle },
          { label: 'Type', value: this.documentTypeName || doc.documentType },
          { label: 'Revision', value: file.revisionNumber, chip: true, chipColor: 'primary', outlined: true },
          versionNumber
            ? {
                label: 'Version',
                value: `v${versionNumber}${this.isOldVersion ? '' : ' (current)'}`,
                chip: true,
                chipColor: this.isOldVersion ? undefined : 'primary',
                outlined: this.isOldVersion,
                pinned: !this.isOldVersion && doc.isCurrentOverridden,
              }
            : null,
          { label: 'Scope', ...scope },
          { label: 'File', value: file.fileName, title: file.fileName },
          { label: 'Size', value: file.size ? this.$formatFileSize(file.size, 1) : '' },
          { label: 'Format', value: file.contentType },
          { label: 'Uploaded by', value: file.createdBy || file.createdByName },
          { label: 'Uploaded on', value: file.createDate ? $formatDate(file.createDate) : '' },
          doc.versionCount ? { label: 'Versions', value: String(doc.versionCount) } : null,
        ].filter((row) => row && row.value);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ws-details {
    padding: 12px 16px;
  }

  .ws-details__list {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr);
    column-gap: 16px;
    row-gap: 10px;
    margin: 0;
  }

  .ws-details__label {
    font-size: var(--ds-font-caption, 12px);
    font-weight: 500;
    opacity: 0.7;
  }

  .ws-details__value {
    margin: 0;
    font-size: var(--ds-font-body-sm, 13px);
    overflow-wrap: anywhere;
  }

  .ws-details__description {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(128, 128, 128, 0.2);

    p {
      margin: 4px 0 0;
      font-size: var(--ds-font-body-sm, 13px);
      white-space: pre-wrap;
    }
  }
</style>
